import { Button } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import layoutStore from '../stores/layoutStore';
import { motion, isValidMotionProp, AnimatePresence } from 'framer-motion';
import {
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Portal,
    Spinner,
    Tooltip,
    textDecoration,
    Link,
    useDisclosure,
    Image
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import usePersistStore from '../stores/persistStore';
import {
    DndContext,
    DragOverlay,
    useSensor,
    useSensors,
    MouseSensor as LibMouseSensor,
    MouseSensorOptions,
    PointerSensor as LibPointerSensor,
    MouseSensor,
    closestCenter,
    useDroppable
} from '@dnd-kit/core';
import useStore from '../stores/store';
import {
    changeWorkspaceFront,
    createWorkspace,
    resolveSharedInvite
} from '../../platformFiles/platformMessages';
import {
    ArrowSmallDownIcon,
    ArrowSmallUpIcon,
    DotsIcons,
    GlobeIcon,
    MotionBox,
    PlusIcon,
    ShareIcon
} from './UiElements';
import EmojiPicker from './EmojiPicker';
import { tooltipAnimation, tooltipDownAnimation } from './customTheme';
import EditableTitle from './EditableTitle';
import DeleteWsModal from './DeleteWsModal';
import { showToast } from '../Utils/showToastWrapper';
import { isOwnerInWs } from './wsApi';
import WorkspacePicker from './WorkspacePicker';
import {
    SortableContext,
    horizontalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useHotkeys } from 'react-hotkeys-hook';
import useWsGridStore from '../stores/wsGridStore';
import { PLATFORM } from '../../platformFiles/utils/platformConstants';

const WorkspacesGrid = () => {
    //global state
    const userData = useStore((state) => state.userData);
    const userStatus = useStore((state) => state.userStatus);
    const newWsLoading = useWsGridStore((state) => state.newWsLoading);

    const currentWsId = useStore((state) => state.currentWsId);
    const workspacesUnderDeletion = useStore(
        (state) => state.workspacesUnderDeletion
    );

    const colorMode = useColorMode().colorMode;
    let activationConstraint = {
        distance: 2
    };
    const dragAndDropSensors = useSensors(
        useSensor(MouseSensor, { activationConstraint })
    );
    const selectedCategory = usePersistStore((state) => state.selectedCategory);
    const showDock = layoutStore((state) => state.showDock);
    //const setIsZooming = layoutStore((state) => state.setIsZooming);
    //const isZooming = layoutStore((state) => state.isZooming);
    const [selectedCatId, setSelectedCatId] = useState(null);

    useEffect(() => {
        let workspace = userData.workspaces.find((ws) => ws.id === currentWsId);
        if (!workspace) {
            console.error('Workspace not found for current wsId:', currentWsId);
            return;
        }
        setSelectedCatId(workspace.catData[selectedCategory].id);
    }, [selectedCategory, currentWsId]);

    //local state
    const [activeDragItem, setActiveDragItem] = useState(null);

    const [overGroup, setOverGroup] = useState(null);
    const [originalWs, setOriginalWs] = useState(null);
    const [dragLoading, setDragLoading] = useState(null);

    const [fallbackWsState, setFallbakWsState] = useState(null);
    const titleColor = useColorModeValue('gray.300', 'gray.600');

    /* const [x, setX] = useState(0);
    const [y, setY] = useState(0);
    const [isPanning, setIsPanning] = useState(false); */
    const subTitle = { light: 'gray.600', dark: '#A1A2A4' };

    const submitNewTitle = (newTitle, wsId) => {
        useStore.getState().updateWsMeta({ name: newTitle }, wsId);
    };

    useEffect(() => {
        useWsGridStore.getState().setloadingWsData(null);
        useWsGridStore.getState().setHighlight(null);
        let activeWsIndex = userData.workspaces.findIndex(
            (ws) => ws.id === currentWsId
        );
        if (scrollContainer.current) {
            const container = scrollContainer.current;
            const workspaceTop = activeWsIndex * 114;
            const workspaceBottom = workspaceTop + 114;

            if (
                workspaceTop < container.scrollTop ||
                workspaceBottom > container.scrollTop + container.clientHeight
            ) {
                container.scrollTo({
                    top: workspaceTop - 40,
                    left: 0,
                    behavior: 'auto'
                });
            }
        }
        showDock('arrows');
        return () => {
            showDock(null);
        };
    }, []);

    const handleDragStart = (active) => {
        if (active.data?.current?.catLength < 2) {
            showToast(
                'At least one category per workspace required',
                'Add more before moving',
                'error'
            );
            throw new Error('At least one category per workspace required');
        }
        setActiveDragItem({
            ...active.data?.current?.catData,
            isCurrentWsAndSelected: active.data?.current?.isCurrentWsAndSelected
        });
        setOriginalWs(active.data?.current?.groupId);
        setFallbakWsState(structuredClone(userData.workspaces));
    };

    const handleDragEnd = (active, over) => {
        if (!over) {
            setActiveDragItem(null);
            setOriginalWs(null);
            setFallbakWsState(null);
            return;
        }
        if (
            originalWs === over.data.current.groupId &&
            over.data.current.sortable.index ===
                active.data.current.sortable.index
        ) {
            setActiveDragItem(null);
            setOriginalWs(null);
            setFallbakWsState(null);
            return;
        }

        let overWsId = over.data.current.groupId;
        let overWorkspace = userData.workspaces.find(
            (ws) => ws.id === overWsId
        );
        if (overWorkspace.catData.length > 8) {
            showToast(
                'Workspace full',
                'Maximum 8 categories per workspace',
                'error'
            );
            if (fallbackWsState !== null) {
                useStore
                    .getState()
                    .updateUserData({ workspaces: fallbackWsState }, false);
                setFallbakWsState(null);
            }
            setActiveDragItem(null);
            setOriginalWs(null);
            return;
        }
        setDragLoading(active.id);

        useStore
            .getState()
            .moveCategory(
                originalWs,
                over.data.current.groupId,
                activeDragItem.id,
                over.data.current.sortable.index,
                fallbackWsState,
                setFallbakWsState,
                setDragLoading
            );
        setActiveDragItem(null);
        setOriginalWs(null);
    };

    const handleDragCancel = () => {
        if (fallbackWsState !== null) {
            useStore
                .getState()
                .updateUserData({ workspaces: fallbackWsState }, false);
            setFallbakWsState(null);
        }
        setActiveDragItem(null);
        setOriginalWs(null);
    };

    let scrollContainer = useRef(null);
    let responsiveWidth =
        PLATFORM === 'web'
            ? ['100vw', '100vw', 'calc(100vw - 96px)']
            : 'calc(100vw - 96px)';

    return (
        <Box>
            <WsShortcutRecorder />
            <Flex alignItems={'center'} m="20px 24px 16px">
                <WorkspacePicker wsVariant={true} />
                <motion.div
                    initial={{
                        x: -14,
                        opacity: 0
                    }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.14 }}
                >
                    <Text
                        ml="4px"
                        fontSize="22px"
                        color={titleColor}
                        fontWeight={600}
                        userSelect={'none'}
                    >
                        Workspaces
                    </Text>
                </motion.div>
            </Flex>
            <MotionBox
                /* onPan={(event, info) => {
                setX((prevX) => prevX + info.delta.x);
                setY((prevY) => prevY + info.delta.y);
            }}
            onPanEnd={() => {
                setIsPanning(false);
            }}
            onPanStart={() => {
                setIsPanning(true);
            }} */
                ref={scrollContainer}
                display="flex"
                flexDir="column"
                h="calc(100vh - 72px)"
                w={responsiveWidth}
                pb="64px"
                flex="1"
                //w="100%" //"calc(100% - 224px)"
                //alignItems="center"
                ml="16px"
                //whileTap={{ cursor: 'grabbing' }}
                cursor="pointer"
                outline={'none'}
                tabIndex={-1}
                overflowY="auto"
                overflowX="hidden"
                initial={{ scale: 1.015, opacity: 0 }}
                animate={{
                    scale: 1,
                    opacity: 1,
                    transition: {
                        duration: 0.6,
                        ease: 'easeOut',
                        opacity: { duration: 0.1 }
                    }
                }}
            >
                <DndContext
                    sensors={dragAndDropSensors}
                    collisionDetection={(args) =>
                        customCatCollisionDetection(
                            {
                                ...args
                            },
                            overGroup,
                            setOverGroup
                        )
                    }
                    //   modifiers={[rest]}
                    onDragStart={({ active }) => handleDragStart(active)}
                    onDragEnd={({ active, over }) =>
                        handleDragEnd(active, over)
                    }
                    onDragCancel={() => handleDragCancel()}
                >
                    {userStatus === 'signedIn'
                        ? userData.workspaces?.map((ws, i) =>
                              ws.sharedInvite ? (
                                  <InviteRow key={ws.id} invite={ws} />
                              ) : (
                                  <WsRow
                                      key={ws.id}
                                      ws={ws}
                                      workspacesUnderDeletion={
                                          workspacesUnderDeletion
                                      }
                                      i={i}
                                      userData={userData}
                                      submitNewTitle={submitNewTitle}
                                      selectedCatId={selectedCatId}
                                      currentWsId={currentWsId}
                                      activeDragItem={activeDragItem}
                                      dragLoading={dragLoading}
                                      newWsLoading={newWsLoading}
                                  />
                              )
                          )
                        : 'No data'}
                    <Tooltip
                        label="New workspace"
                        zIndex="500000"
                        motionProps={tooltipAnimation}
                        rounded="6px"
                        placement="top"
                        isDisabled={
                            userData.stripeSubscriptionStatus !== 'active'
                        }
                    >
                        <Button
                            ml="42px"
                            onClick={() => createWorkspace()}
                            w="244px"
                            h="118px"
                            minH="118px"
                            rounded="12px"
                            mt="16px"
                            fontSize="16px"
                            color={'iconMd'}
                            opacity={0.825}
                            _hover={{ opacity: 1, bg: 'te1' }}
                            _active={{ bg: 'te1', transform: 'scale(0.9625)' }}
                            isDisabled={
                                userData.stripeSubscriptionStatus !== 'active'
                            }
                            isLoading={newWsLoading}
                        >
                            ＋
                        </Button>
                    </Tooltip>
                    {userData.stripeSubscriptionStatus !== 'active' && (
                        <Text
                            color={subTitle[colorMode]}
                            px="42px"
                            mt="14px"
                            textAlign="left"
                            letterSpacing="0.34px"
                        >
                            <Link
                                href="https://www.tabextend.com/pricing"
                                // alt="See pricing"
                                textDecoration="underline"
                            >
                                Upgrade your plan
                            </Link>{' '}
                            to create more{' '}
                            <Link
                                href="https://www.tabextend.com/guide/workspaces"
                                // alt="See pricing"
                                textDecoration="underline"
                            >
                                workspaces
                            </Link>
                        </Text>
                    )}
                    <Portal>
                        <DragOverlay>
                            <WsCategory
                                key={activeDragItem?.id + 'dnd'}
                                c={{
                                    ...activeDragItem,
                                    id: activeDragItem?.id + 'dnd'
                                }}
                                i={0}
                                isCurrentWsAndSelected={
                                    activeDragItem?.isCurrentWsAndSelected
                                }
                                ws={{ id: 1 }}
                                dragLoading={null}
                            />
                        </DragOverlay>
                    </Portal>
                </DndContext>
            </MotionBox>
        </Box>
    );
};

const WsShortcutRecorder = () => {
    const highlight = useWsGridStore((state) => state.highlighted);
    const moveHighlight = useWsGridStore((state) => state.moveHighlight);
    const onEnter = () => {
        let currentHighlight = useWsGridStore.getState().highlighted;
        useWsGridStore
            .getState()
            .changeWorkspace(currentHighlight.WSid, currentHighlight.cat);
    };

    useHotkeys(
        'up',
        (e) => {
            e.preventDefault();
            moveHighlight('up');
        },
        [highlight]
    );
    useHotkeys(
        'down',
        (e) => {
            e.preventDefault();
            moveHighlight('down');
        },
        [highlight]
    );
    useHotkeys(
        'left',
        (e) => {
            e.preventDefault();
            moveHighlight('left');
        },
        [highlight]
    );
    useHotkeys(
        'right',
        (e) => {
            e.preventDefault();
            moveHighlight('right');
        },
        [highlight]
    );
    useHotkeys(
        'enter',
        (e) => {
            e.preventDefault();
            onEnter();
        },
        [highlight]
    );

    return null;
};

const WsRowDroppable = ({ id, children }) => {
    const { setNodeRef } = useDroppable({
        id: id,
        data: { type: 'group' }
    });
    return <div ref={setNodeRef}>{children}</div>;
};

export default WorkspacesGrid;

const WsCategoryDnd = ({
    c,
    i,
    selectedCatId,
    currentWsId,
    ws,
    dragLoading
}) => {
    // let isSelected = i === selectedCatId;
    // let isCurrentWs = ws.id === currentWsId;
    let isCurrentWsAndSelected = selectedCatId === c.id;

    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition
    } = useSortable({
        id: c.id,
        data: {
            catData: c,
            groupId: ws.id,
            catLength: ws.catData.length,
            isCurrentWsAndSelected: isCurrentWsAndSelected
        }
        // transition: {
        //     duration: 450,
        //     easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
        // }
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    return (
        <div
            ref={setNodeRef}
            style={{ opacity: isDragging ? 0.5 : '', ...style }}
            {...attributes}
            {...listeners}
        >
            <WsCategory
                // key={c.id}
                c={c}
                i={i}
                isCurrentWsAndSelected={isCurrentWsAndSelected}
                ws={ws}
                dragLoading={dragLoading}
            />
        </div>
    );
};

const WsRow = ({
    ws,
    workspacesUnderDeletion,
    i,
    userData,
    submitNewTitle,
    selectedCatId,
    currentWsId,
    activeDragItem,
    dragLoading,
    newWsLoading
}) => {
    let isPublic = ws.public;

    return (
        <SortableContext
            items={ws.catData}
            strategy={horizontalListSortingStrategy}
        >
            <WsRowDroppable id={ws.id}>
                <Box
                    key={ws.id}
                    role="group"
                    pos="relative"
                    opacity={workspacesUnderDeletion.includes(ws.id) ? 0.5 : ''}
                    sx={{
                        pointerEvents:
                            workspacesUnderDeletion.includes(ws.id) ||
                            dragLoading !== null ||
                            newWsLoading
                                ? 'none'
                                : 'auto'
                    }}
                >
                    <Flex
                        m="4px 0 4px 0px"
                        alignItems="center"
                        p="0 8px"
                        /* transition={'0.2s'}
                                  _groupHover={{
                                      transform: 'translateX(16px)'
                                  }} */
                    >
                        <WorkspaceMenu
                            wsName={ws.name}
                            id={ws.id}
                            firstOrLastIndex={
                                i === 0
                                    ? 'first'
                                    : i === userData.workspaces.length - 1
                                    ? 'last'
                                    : null
                            }
                            isOwner={isOwnerInWs(ws.shared, ws.owner, userData)}
                            isPublic={isPublic}
                        />
                        <>
                            <Tooltip
                                label="Edit symbol"
                                zIndex="500000"
                                placement="top"
                                rounded="6px"
                                motionProps={tooltipAnimation}
                            >
                                <div>
                                    <EmojiPicker
                                        emoji={ws.emoji}
                                        type={'workspace'}
                                        id={ws.id}
                                        //ml={'96px'}
                                    />
                                </div>
                            </Tooltip>
                            <Tooltip
                                label="Edit title"
                                zIndex="500000"
                                placement="top"
                                rounded="6px"
                                motionProps={tooltipAnimation}
                            >
                                <div>
                                    <EditableTitle
                                        ml={'8px'}
                                        title={ws.name}
                                        size={'workspace'}
                                        onSubmit={(newTitle) =>
                                            submitNewTitle(newTitle, ws.id)
                                        }
                                    />
                                </div>
                            </Tooltip>
                        </>
                    </Flex>
                    <Flex
                        //overflowX={isZooming ? 'visible' : 'auto'}
                        height="78px"
                        maxW="100%" //"calc(100% - 360px)"
                        gap="12px"
                        p="4px 8px 4px 12px"
                        ml="30px"
                        overflowX="auto"
                        /* opacity={wsDataLoading !== null ? 0.675 : 1} */
                    >
                        {ws.catData.map((c, i) => (
                            <WsCategoryDnd
                                key={c.id}
                                c={c}
                                i={i}
                                selectedCatId={selectedCatId}
                                currentWsId={currentWsId}
                                ws={ws}
                                dragLoading={dragLoading}
                            />
                        ))}
                    </Flex>
                </Box>
            </WsRowDroppable>
        </SortableContext>
    );
};

const WsCategory = ({ c, i, isCurrentWsAndSelected, ws, dragLoading }) => {
    const highlighted = useWsGridStore((state) => state.highlighted);
    const setHighlight = useWsGridStore((state) => state.setHighlight);
    let isDragLoading = dragLoading === c.id;
    //isSelected && console.log('ws.id: ', ws.id, 'currentWsId: ', currentWsId);
    let isHighlighted = highlighted?.cat === i && highlighted?.WSid === ws.id;

    /* const [initDone, setInit] = useState(false);

    const toggleAnimationDone = (dir) => {
        if (
            dir.boxShadow ===
            '0px 0px 0px 2px #50A2FE, rgba(49, 144, 255, 0.54) 0px 7px 18px -10px'
        ) {
            setInit(true);
        }
    }; */

    const hoverOver = () => {
        if (isCurrentWsAndSelected) {
            setHighlight(null);
        } else setHighlight({ WSid: ws.id, cat: i });
    };

    const loadingWsData = useWsGridStore((state) => state.loadingWsData);
    const isLoading = loadingWsData?.WSid === ws.id && loadingWsData?.cat === i;
    const changeCatAndWs = useWsGridStore((state) => state.changeWorkspace);

    let ref = useRef(null);

    /* 
    TODO -> create middlewhere for changeCat and get XY for zoom
    28/3 Did not use this because of possible performance issues and time constraints
    const setZoomOutXY = () => {
        let rect = ref.current?.getBoundingClientRect();
        let x = rect.left + rect.width / 2 - 72;
        let y = rect.top + rect.height / 2 - 72;
        let originX = x / (window.innerWidth - 72);
        let originY = y / (window.innerHeight - 72);
        layoutStore.getState().setZoomXY(originX, originY);
    };

    useEffect(() => {
        if (isCurrentWsAndSelected) {
            setZoomOutXY();
        }
    }, [isCurrentWsAndSelected]); */

    return (
        <MotionBox
            ref={ref}
            // key={c.id}
            onClick={() => changeCatAndWs(ws.id, i)}
            //as={motion.div}
            //layoutId={isCurrentWsAndSelected ? 'workspace-container' : null}
            bg={'te1'}
            p="8px 12px"
            // bg={'te1'}
            h="54px" //124
            minW="124px" //244
            w="124px" //244
            rounded="12px"
            shadow={'sm'}
            //@ts-ignore
            transition={{
                /* layout: {
                    duration: 0.32,
                    type: 'spring',
                    bounce: 0.24
                }, */
                //boxShadow: {
                //duration: initDone ? 0.12 : 0.24
                //delay: 0.16
                //},
                scale: {
                    duration: 0.16,
                    type: 'spring'
                    //bounce: 0.2
                }
            }}
            style={{ borderRadius: '12px' }}
            outline={'none'}
            initial={{
                boxShadow: isCurrentWsAndSelected
                    ? '0px 0px 0px 0px #50A2FE'
                    : ''
            }}
            animate={{
                //scale: isCurrentWsAndSelected ? (initDone ? 1 : 1.125) : 1, initDone seems to not always work..
                boxShadow: isHighlighted
                    ? '0px 0px 0px 2px #50A2FE, rgba(49, 144, 255, 0.54) 0px 7px 18px -10px'
                    : isCurrentWsAndSelected
                    ? highlighted === null
                        ? '0px 0px 0px 2px #50A2FE, rgba(49, 144, 255, 0.54) 0px 7px 18px -10px'
                        : '0px 0px 0px 2px #50A2FE64'
                    : ''
            }}
            whileHover={{
                scale: 1.0325
                //boxShadow: '0px 0px 0px 2px #50A2FE'
            }}
            whileTap={{
                scale: 0.9825,
                boxShadow: '0px 0px 0px 2px #50A2FE'
            }}
            cursor="pointer"
            onMouseEnter={hoverOver}
            onMouseOver={hoverOver}
            //onAnimationComplete={(dir) => toggleAnimationDone(dir)}
            role="group"
            pos="relative"
            overflow={'hidden'}
        >
            <Text
                fontSize="13px"
                fontWeight={600}
                opacity={isHighlighted ? 1 : 0.725}
                //_groupHover={{ opacity: 1 }}
                transition={'0.16s'}
                userSelect={'none'}
                isTruncated
            >
                {c.name || 'Untitled'}
            </Text>
            {(isDragLoading || isLoading) && (
                <Flex
                    pos="absolute"
                    top="0"
                    left="0"
                    w="100%"
                    h="100%"
                    // bg="rgba(0,0,0, 0.2)"
                    style={{ backdropFilter: 'blur(1px)' }}
                    display={'flex'}
                    alignItems={'center'}
                >
                    <Spinner color="blue.300" size="sm" m="auto" />
                </Flex>
            )}
        </MotionBox>
    );
};

// const MiniSpaces = ({ groupsToShow, isShared, isMarked, isChosen }) => {
//     const title = useColorModeValue('#1A202C', '#FFF');
//     const miniSpaceBg = useColorModeValue('#FFF', '#35393c');
//     const globeMarked = useColorModeValue('#3391FF', '#FFFFFF60');
//     const globeChosen = useColorModeValue('#9ECDFE', '#FFFFFF60');
//     const miniSpaceShadows = useColorModeValue(
//         '0 4px 6px -2px rgba(165,182,185,0.36), 0 2px 8px -1px rgba(171,174,181,0.18)',
//         '0 40px 40px -30px rgba(0,0,0,0.50), 0 8px 16px -4px rgba(0,0,0,0.30)'
//     );
//     const emptyStateText = useColorModeValue('gray.600', '#A1A2A4');
//     const iconColor = useColorModeValue('gray.500', '#FFFFFF60');
//     console.log('groupsToShow: ', groupsToShow);
//     const groupsLength = 4; //groupsToShow.length;
//     let spaceW = '18px';
//     let spaceH = '28px';
//     let threeCats = [...Array(3)];
//     let test = [...Array(4)];

//     return (
//         <>
//             {isShared ? (
//                 <Icon
//                     //name="globeIcon"
//                     m="auto"
//                     ml="8px"
//                     boxSize="22px"
//                     mt="6px"
//                     color={
//                         isMarked
//                             ? globeMarked
//                             : isChosen
//                             ? globeChosen
//                             : iconColor
//                     }
//                 />
//             ) : groupsLength > 0 && groupsLength < 5 ? (
//                 test.map((group, index) => (
//                     <Flex
//                         key={index}
//                         //alignItems="center"
//                         h={spaceH}
//                         w={spaceW}
//                         //px="4px"
//                         //overflow="hidden"
//                         bg={miniSpaceBg}
//                         boxShadow={miniSpaceShadows}
//                         rounded="4px"
//                         ml="6px"
//                     >
//                         {/* <Text
//                         fontWeight="600"
//                         mt="4px"
//                         fontSize="11px"
//                         maxW="100%"
//                         isTruncated
//                     >
//                         {
//                             group.title
//                         }
//                     </Text> */}
//                     </Flex>
//                 ))
//             ) : groupsLength > 4 ? (
//                 <>
//                     {threeCats.map((group, index) => (
//                         <Flex
//                             key={index}
//                             //alignItems="center"
//                             h={spaceH}
//                             w={spaceW}
//                             //px="4px"
//                             //overflow="hidden"
//                             bg={miniSpaceBg}
//                             boxShadow={miniSpaceShadows}
//                             rounded="4px"
//                             ml="6px"
//                         ></Flex>
//                     ))}
//                     <Flex
//                         //alignItems="center"
//                         h={spaceH}
//                         w={spaceW}
//                         //px="4px"
//                         //overflow="hidden"
//                         bg={miniSpaceBg}
//                         boxShadow={miniSpaceShadows}
//                         rounded="4px"
//                         ml="6px"
//                     >
//                         <Text
//                             m="auto"
//                             color={title}
//                             fontSize="11px"
//                             fontWeight="600"
//                             opacity="0.6"
//                         >
//                             +{groupsLength - 4}
//                         </Text>
//                     </Flex>
//                 </>
//             ) : (
//                 <Text
//                     color={emptyStateText}
//                     fontWeight="700"
//                     opacity="0.75"
//                     fontSize="8px"
//                     letterSpacing="0.16px"
//                     m="auto"
//                     ml="8px"
//                 >
//                     EMPTY CATEGORY
//                 </Text>
//             )}
//         </>
//     );
// };

const WorkspaceMenu = ({ wsName, id, firstOrLastIndex, isOwner, isPublic }) => {
    const setShowShareModal = layoutStore((state) => state.setShowShareModal);
    const borderBottom = useColorModeValue(
        '1px solid #e4eaf0',
        '1px solid #394146'
    );
    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const lightBg = useColorModeValue('gray.50', '#31363a');
    const menuProps = {
        bg: { menuBg },
        p: '8px 16px 8px 8px',
        rounded: '6px',
        fontWeight: 600,
        transition: '0.1s',
        //margin: '4px',
        _hover: { bg: lightBg }
    };
    const deleteBg = useColorModeValue('red.100', '#552F32');
    const hoverButton = useColorModeValue('gray.300', '#32363A');

    const { isOpen, onOpen, onClose } = useDisclosure();

    const openShareModal = () => {
        setShowShareModal(true, id);
    };

    const moveWsUp = () => {
        useStore.getState().changeWsIndex(id, 'up');
    };
    const moveWsDown = () => {
        useStore.getState().changeWsIndex(id, 'down');
    };

    return (
        <>
            <Menu isLazy>
                {({ isOpen }) => (
                    <>
                        <MenuButton
                            //pos="absolute"
                            //top="0"
                            //left="0"
                            mr="4px"
                            //ml="auto"
                            as={IconButton}
                            aria-label="Workspace options"
                            icon={<DotsIcons />}
                            bg="transparent"
                            h="32px"
                            w="32px"
                            minW="32px"
                            maxW="32px"
                            maxH={'32px'}
                            rounded="7px"
                            p="0"
                            alignItems={'center'}
                            //opacity={hover ? 0 : 1}
                            opacity={isOpen ? 0.75 : 0}
                            zIndex={19}
                            _groupHover={{ opacity: 1, zIndex: 20 }}
                            _hover={{ bg: hoverButton }}
                            transition={'0.2s'}
                            _active={{
                                transform: 'scale(0.925)'
                            }}
                        />
                        {isPublic && (
                            <Box
                                pos="absolute"
                                left="16px"
                                top="8px"
                                transform={isOpen ? 'scale(0)' : 'scale(1)'}
                                _groupHover={{ transform: 'scale(0)' }}
                                transition={'0.1s'}
                            >
                                <GlobeIcon color={'#50A2FE'} />
                            </Box>
                        )}
                        <AnimatePresence>
                            {isOpen && (
                                <Portal>
                                    <MenuList
                                        as={motion.div}
                                        initial={{ y: -8, opacity: 0.4 }}
                                        animate={{
                                            y: 0,
                                            opacity: 1,
                                            transition: {
                                                duration: 0.12,
                                                ease: [0.17, 0.67, 0.83, 0.67]
                                            }
                                        }}
                                        bg={menuBg}
                                        fontSize={'15px'}
                                        p="4px"
                                        border="none"
                                        rounded="8px"
                                        boxShadow={
                                            'var(--chakra-colors-popoverShadowLight)'
                                        }
                                        minW="164px"
                                        zIndex={99900}
                                    >
                                        <MenuItem
                                            onClick={moveWsUp}
                                            icon={<ArrowSmallUpIcon />}
                                            {...menuProps}
                                            isDisabled={
                                                firstOrLastIndex === 'first'
                                            }
                                        >
                                            Move up
                                        </MenuItem>
                                        <MenuItem
                                            onClick={moveWsDown}
                                            icon={<ArrowSmallDownIcon />}
                                            {...menuProps}
                                            isDisabled={
                                                firstOrLastIndex === 'last'
                                            }
                                        >
                                            Move down
                                        </MenuItem>
                                        <MenuItem
                                            onClick={openShareModal}
                                            icon={<ShareIcon />}
                                            {...menuProps}
                                        >
                                            Share
                                        </MenuItem>
                                        <MenuItem
                                            onClick={onOpen}
                                            icon={
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="#ea5b5b"
                                                    style={{
                                                        width: '16px',
                                                        height: '16px'
                                                    }}
                                                >
                                                    <path
                                                        d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
                                                        fillRule="evenodd"
                                                    />
                                                </svg>
                                            }
                                            {...menuProps}
                                            _hover={{
                                                bg: deleteBg,
                                                filter: 'brightness(1.1)'
                                            }}
                                            color="red.500"
                                        >
                                            Delete
                                        </MenuItem>
                                    </MenuList>
                                </Portal>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </Menu>
            <DeleteWsModal
                isOpen={isOpen}
                onClose={onClose}
                wsName={wsName}
                isOwner={isOwner}
                id={id}
            />
        </>
    );
};

const InviteRow = ({ invite }) => {
    const { id, emoji, name, ownerEmail } = invite;
    let size = '32px';
    const userPlan = useStore.getState().userData?.stripeSubscriptionStatus;
    const disableAccept = userPlan !== 'active';
    const [isLoading, setIsLoading] = useState(false);

    const acceptInvite = (accepted) => {
        setIsLoading(true);
        resolveSharedInvite({
            accepted: accepted,
            wsId: id
        }).then((resp) => {
            setIsLoading(false);
            if (resp?.data?.success) {
                if (accepted) {
                    showToast(
                        'Invite accepted successfully',
                        `You now have access to ${name}`,
                        'success'
                    );
                } else {
                    showToast(
                        'Invite decliend',
                        'Workspace invite removed',
                        'success'
                    );
                }
            } else {
                console.error('error responding invite', resp);
                showToast('Something went wrong', 'Please try again', 'error');
            }
        });
    };

    const tooltipContent = disableAccept ? (
        <>
            <Text>{'Invited by: ' + ownerEmail}</Text>
            <Text fontSize="11px" opacity={0.8}>
                Upgrade your plan to accept and collaborate
            </Text>
        </>
    ) : (
        'Invited by: ' + ownerEmail
    );

    return (
        <Tooltip
            label={tooltipContent}
            zIndex="500000"
            rounded="6px"
            motionProps={tooltipDownAnimation}
        >
            <Box
                key={id}
                role="group"
                pos="relative"
                ml="34px"
                minH="96px"
                boxShadow="inset 0 0 0 2px #41E18B"
                bg="rgb(65, 225, 139, 0.1)"
                rounded="12px"
                p="8px"
                w="fit-content"
                minW="404px"
                mb="4px"
            >
                <Flex m="4px 0 4px 0px" alignItems="center" p="0">
                    <Box
                        rounded="8px"
                        height={size}
                        maxWidth={size}
                        minW={size}
                        minH={size}
                        display={'flex'}
                        alignItems={'center'}
                    >
                        {emoji?.length > 10 ? (
                            <Image
                                src={emoji}
                                boxSize={'18px'}
                                overflow="hidden"
                                rounded={'2px'}
                                m="auto"
                            />
                        ) : (
                            <Text fontSize="18px" m="auto">
                                {emoji}
                            </Text>
                        )}
                    </Box>
                    <Text fontSize="16px" fontWeight={'600'} ml="8px">
                        {name}
                    </Text>
                </Flex>

                <Flex ml="4px">
                    <Button
                        rounded="8px"
                        mr="8px"
                        isLoading={isLoading}
                        onClick={() => acceptInvite(true)}
                        size="sm"
                        color="#11b35c"
                        _hover={{ filter: 'brightness(1.1)' }}
                        isDisabled={disableAccept}
                    >
                        Accept invite
                    </Button>
                    <Button
                        rounded="8px"
                        onClick={() => acceptInvite(false)}
                        isLoading={isLoading}
                        size="sm"
                        color="#ff3636"
                        _hover={{ filter: 'brightness(1.1)' }}
                    >
                        Decline
                    </Button>
                </Flex>
            </Box>
        </Tooltip>
    );
};

const customCatCollisionDetection = (args, overGroup, setOverGroup) => {
    let filteredGroupContainers = args.droppableContainers.filter(
        (container) => container.data.current.type === 'group'
    );
    // console.log('args.droppableContainers', args.droppableContainers);
    // const mouseY = args.pointerCoordinates.y;
    const centerRect = centerOfRectangle(
        args.collisionRect,
        args.collisionRect.left,
        args.collisionRect.top
    );
    const closestGroup = filteredGroupContainers.reduce(
        (closest, container) => {
            const rect = container.rect.current;
            const containerTop = rect.top + 1;
            const containerBottom = rect.bottom - 1;

            const centerToTopDistance = Math.abs(centerRect.y - containerTop);
            const centerToBottomDistance = Math.abs(
                centerRect.y - containerBottom
            );
            // Use the smaller distance (either to top or to bottom)
            const distance = Math.min(
                centerToTopDistance,
                centerToBottomDistance
            );
            if (!closest || distance < closest.distance) {
                return { container, distance };
            }
            return closest;
        },
        null
    )?.container;

    const overContainerRaw = closestGroup.id;

    let filteredTabContainers = args.droppableContainers.filter(
        (container) => container.data.current.groupId === overContainerRaw
    );
    // console.log('filteredTabContainers', filteredTabContainers);

    if (filteredTabContainers.length === 0) {
        if (overGroup === null) {
            setOverGroup(overContainerRaw);
        } else if (overGroup !== overContainerRaw) {
            setOverGroup(overContainerRaw);
        }

        return null;
    } else {
        let newOverItem = closestCenter({
            ...args,
            droppableContainers: filteredTabContainers
        });

        // console.log('overGroup', collisionState.overGroup);
        // console.log('overContainerRaw', overContainerRaw);
        if (overGroup === null) {
            setOverGroup(overContainerRaw);
        } else if (overGroup !== overContainerRaw) {
            // const centerRect = centerOfRectangle(
            //     args.collisionRect,
            //     args.collisionRect.left,
            //     args.collisionRect.top
            // );
            let overContainerRightSide =
                newOverItem[0].data.droppableContainer.rect.current.right;
            let afterLast = false;
            if (overContainerRightSide < centerRect.x) {
                afterLast = true;
            }
            moveCatStateToNewWs(
                args.active.data.current.catData.id,
                newOverItem[0].data.droppableContainer.data.current.catData.id,
                overGroup,
                overContainerRaw,
                afterLast
            );
            setOverGroup(overContainerRaw);
        }

        return newOverItem;
    }
};

const moveCatStateToNewWs = (catId, overId, oldWsId, newWsId, afterLast) => {
    // return;
    let userData = useStore.getState().userData;
    let workspaces = structuredClone(userData.workspaces);
    const oldWorkspaceIndex = workspaces.findIndex((ws) => ws.id === oldWsId);
    let categoryToMove = null;

    if (oldWorkspaceIndex !== -1) {
        const catIndex = workspaces[oldWorkspaceIndex].catData.findIndex(
            (cat) => cat.id === catId
        );
        if (catIndex !== -1) {
            // Save the category
            categoryToMove = workspaces[oldWorkspaceIndex].catData[catIndex];
            // Remove the category from the old workspace
            workspaces[oldWorkspaceIndex].catData.splice(catIndex, 1);
            // Update catLength for the old workspace
            workspaces[oldWorkspaceIndex].catLength =
                workspaces[oldWorkspaceIndex].catData.length;
        } else {
            // throw new Error('Error updating ws state');
        }
    }

    // Then, add the category to the new workspace
    if (categoryToMove) {
        const newWorkspaceIndex = workspaces.findIndex(
            (ws) => ws.id === newWsId
        );

        if (newWorkspaceIndex !== -1) {
            let overItemIndex = workspaces[newWorkspaceIndex].catData.findIndex(
                (cat) => cat.id === overId
            );
            if (afterLast) {
                workspaces[newWorkspaceIndex].catData.push(categoryToMove);
            } else {
                workspaces[newWorkspaceIndex].catData.splice(
                    overItemIndex,
                    0,
                    categoryToMove
                );
            }
            // Update catLength for the new workspace
            workspaces[newWorkspaceIndex].catLength =
                workspaces[newWorkspaceIndex].catData.length;
        } else {
            // throw new Error('Error updating ws state');
        }
    } else {
        // throw new Error('Error updating ws state');
    }
    useStore.getState().updateUserData({ workspaces: workspaces }, false);
};

export function centerOfRectangle(rect, left = rect.left, top = rect.top) {
    return {
        x: left + rect.width * 0.5,
        y: top + rect.height * 0.5
    };
}
